import React, { useState, useEffect } from 'react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isBefore,
  startOfDay
} from 'date-fns';
import { HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineMapPin, HiOutlineClock, HiOutlineUsers, HiOutlineCheckCircle, HiOutlineStar, HiOutlineUser, HiOutlineGlobeAlt, HiOutlineInformationCircle, HiOutlineXCircle, HiOutlineTruck, HiOutlineCheckBadge } from 'react-icons/hi2';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { useAuth } from '../../contexts/AuthContext';
import './ExperienceDetailPage.css';

const ExperienceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isApproved, affiliateTier } = useAuth();

  const [experience, setExperience] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [guestCount, setGuestCount] = useState(1);
  const [bookedGuestsCount, setBookedGuestsCount] = useState(0);
  const [availableSpots, setAvailableSpots] = useState(null);
  const [availableSpotsPerOption, setAvailableSpotsPerOption] = useState({});
  const [loadingSpots, setLoadingSpots] = useState(false);
  const [reviewStats, setReviewStats] = useState({
    totalNumberOfReviews: 0,
    averageRating: 0,
    totalCleanlinessPoints: 0,
    totalCommunicationPoints: 0,
    totalServicePoints: 0
  });
  const [reviews, setReviews] = useState([]);
  const [visibleReviews, setVisibleReviews] = useState(3);

  useEffect(() => {
    const fetchExperienceData = async () => {
      try {
        setLoading(true);
        const experienceDoc = await getDoc(doc(db, 'experiences', id));
        if (!experienceDoc.exists()) {
          setError('Experience not found');
          setLoading(false);
          return;
        }
        const experienceData = { id: experienceDoc.id, ...experienceDoc.data() };
        setExperience(experienceData);

        const optionsDoc = await getDoc(doc(db, 'experienceOptions', id));
        if (optionsDoc.exists()) {
          setOptions(optionsDoc.data().options || []);
        }

        const reviewStatsDoc = await getDoc(doc(db, 'experienceReviews', id));
        if (reviewStatsDoc.exists()) {
          const data = reviewStatsDoc.data();
          setReviewStats({
            totalNumberOfReviews: data.totalNumberOfReviews || 0,
            averageRating: data.averageRating ? Number(data.averageRating.toFixed(1)) : 0,
            totalCleanlinessPoints: data.totalCleanlinessPoints || 0,
            totalCommunicationPoints: data.totalCommunicationPoints || 0,
            totalServicePoints: data.totalServicePoints || 0
          });
        }

        const reviewsRef = collection(db, 'experienceReviews', id, 'reviews');
        const reviewsSnapshot = await getDocs(reviewsRef);
        const reviewsData = reviewsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));
        reviewsData.sort((a, b) => b.createdAt - a.createdAt);
        setReviews(reviewsData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching experience:', err);
        setError('Failed to load experience details');
        setLoading(false);
      }
    };
    fetchExperienceData();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const formatRating = (points, total) => {
    if (!total || total === 0) return '0.0';
    return (points / total).toFixed(1);
  };

  const calculatePercentage = (points, total) => {
    if (!total || total === 0) return 0;
    return (points / total / 5) * 100;
  };

  const nextImage = () => {
    if (experience?.images?.length > 1) {
      setCurrentImageIndex((prev) => prev === experience.images.length - 1 ? 0 : prev + 1);
    }
  };

  const prevImage = () => {
    if (experience?.images?.length > 1) {
      setCurrentImageIndex((prev) => prev === 0 ? experience.images.length - 1 : prev - 1);
    }
  };

  const getAffiliatePriceForOption = (option) => {
    if (!option.affiliatePricing?.enabled) return null;
    const tier = affiliateTier || 1;
    // Use display price (with 1.5x margin) first, fall back to legacy tier price for backward compat
    const displayPrice = option.affiliatePricing[`tier${tier}DisplayPrice`];
    if (displayPrice !== undefined && displayPrice !== null) return displayPrice;
    const tierPrice = option.affiliatePricing[`tier${tier}Price`];
    return tierPrice !== undefined && tierPrice !== null ? tierPrice : null;
  };

  const getAffiliateMinPriceForOption = (option) => {
    if (!option.affiliatePricing?.enabled || option.type !== 'Private Experience') return null;
    const tier = affiliateTier || 1;
    const minDisplayPrice = option.affiliatePricing[`tier${tier}MinDisplayPrice`];
    if (minDisplayPrice !== undefined && minDisplayPrice !== null) return minDisplayPrice;
    const minPrice = option.affiliatePricing[`tier${tier}MinPrice`];
    return minPrice !== undefined && minPrice !== null ? minPrice : null;
  };

  const getRegularPriceForOption = (option) => {
    if (option.type === 'Group Experience') {
      return option.pricePerPerson || 0;
    }
    return option.privateMinPrice || 0;
  };

  const getDisplayPrice = () => {
    if (selectedOption) {
      if (selectedOption.type === 'Group Experience') {
        const affiliatePrice = getAffiliatePriceForOption(selectedOption);
        if (affiliatePrice !== null && isApproved) {
          return { price: affiliatePrice, isPrivate: false, isAffiliate: true };
        }
        return { price: selectedOption.pricePerPerson || 0, isPrivate: false };
      } else {
        const affMin = getAffiliateMinPriceForOption(selectedOption);
        const affPP = getAffiliatePriceForOption(selectedOption);
        if ((affMin !== null || affPP !== null) && isApproved) {
          const displayMin = affMin || 0;
          const displayPP = affPP || 0;
          const price = displayMin > 0 ? displayMin : displayPP;
          return { price, isPrivate: true, isAffiliate: true };
        }
        return { price: selectedOption.privateMinPrice || 0, isPrivate: true };
      }
    }
    if (options && options.length > 0) {
      const prices = options.map(opt => {
        const affPrice = getAffiliatePriceForOption(opt);
        if (affPrice !== null && isApproved) return affPrice;
        return opt.type === 'Group Experience' ? opt.pricePerPerson : opt.privateMinPrice;
      }).filter(p => p !== undefined && p !== null);
      if (prices.length > 0) {
        return { price: Math.min(...prices), isPrivate: false };
      }
    }
    return { price: 0, isPrivate: false };
  };

  const getTotalPrice = () => {
    if (!selectedOption) return { regular: 0, affiliate: 0 };
    if (selectedOption.type === 'Group Experience') {
      const affiliatePrice = getAffiliatePriceForOption(selectedOption);
      const regular = (selectedOption.pricePerPerson || 0) * guestCount;
      const affiliate = affiliatePrice !== null ? affiliatePrice * guestCount : null;
      return { regular, affiliate };
    } else {
      const minPrice = selectedOption.privateMinPrice || 0;
      const pricePerPerson = selectedOption.privatePricePerPerson || 0;
      const regular = Math.max(minPrice, pricePerPerson * guestCount);
      // For private: use affiliate min price and per-person price with same Max logic
      const affMin = getAffiliateMinPriceForOption(selectedOption);
      const affPP = getAffiliatePriceForOption(selectedOption);
      let affiliate = null;
      if (affMin !== null || affPP !== null) {
        const aMin = affMin || 0;
        const aPP = affPP || 0;
        affiliate = Math.max(aMin, aPP * guestCount);
        // If both are 0, it's free
        if (aMin === 0 && aPP === 0 && (affMin !== null || affPP !== null)) affiliate = 0;
      }
      return { regular, affiliate };
    }
  };

  const handleBookNow = () => {
    if (!selectedOption) {
      alert('Please select an option');
      return;
    }
    if (!selectedDate) {
      alert('Please select a date');
      return;
    }
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isApproved) {
      alert('Your affiliate account must be approved to book');
      return;
    }
    const prices = getTotalPrice();
    const affiliatePrice = prices.affiliate !== null ? prices.affiliate : prices.regular;
    const discount = prices.affiliate !== null ? Math.round(((prices.regular - prices.affiliate) / prices.regular) * 100) : 0;

    navigate('/experience-booking', {
      state: {
        experienceId: id,
        experienceName: experience.name,
        experienceImage: experience.images?.[0] || '',
        hostId: experience.hostId,
        hostName: experience.hostName,
        hostPhone: experience.hostPhone,
        option: selectedOption,
        date: format(selectedDate, 'yyyy-MM-dd'),
        guestCount: guestCount,
        totalPrice: affiliatePrice,
        regularPrice: prices.regular,
        discount: discount,
        meetingPoint: experience.meetingPoint,
        meetingPointDescription: experience.meetingPointDescription,
        googleMapsLink: experience.googleMapsLink
      }
    });
  };

  const isDateAvailable = (date) => {
    if (!options || options.length === 0) return false;
    const today = startOfDay(new Date());
    if (isBefore(date, today)) return false;
    const availableOptions = getOptionsForDate(date);
    return availableOptions.length > 0;
  };

  const getOptionsForDate = (date) => {
    if (!date || !options) return [];
    const dayName = format(date, 'EEEE');
    const selectedDateObj = startOfDay(date);
    const dateKey = format(date, 'yyyy-MM-dd');
    
    return options.filter(opt => {
      if (!opt.availableDays?.includes(dayName)) return false;
      if (opt.disabledDates && opt.disabledDates.includes(dateKey)) return false;
      if (!opt.seasonStartDate && !opt.seasonEndDate) return true;
      const startDate = opt.seasonStartDate ? startOfDay(new Date(opt.seasonStartDate)) : null;
      const endDate = opt.seasonEndDate ? startOfDay(new Date(opt.seasonEndDate)) : null;
      if (startDate && endDate) return selectedDateObj >= startDate && selectedDateObj <= endDate;
      if (startDate) return selectedDateObj >= startDate;
      if (endDate) return selectedDateObj <= endDate;
      return true;
    }).map(opt => {
      if (opt.dateOverrides && opt.dateOverrides[dateKey]) {
        return { ...opt, ...opt.dateOverrides[dateKey] };
      }
      return opt;
    });
  };

  const availableOptionsForSelectedDate = selectedDate ? getOptionsForDate(selectedDate).filter(opt => {
    const today = startOfDay(new Date());
    const isToday = startOfDay(selectedDate).getTime() === today.getTime();
    if (isToday && opt.startTime) {
      const now = new Date();
      const [hours, minutes] = opt.startTime.split(':').map(Number);
      const optionStartTime = new Date();
      optionStartTime.setHours(hours, minutes, 0, 0);
      const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
      return optionStartTime >= oneHourFromNow;
    }
    return true;
  }) : [];

  const getDaysInMonth = (date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    return eachDayOfInterval({ start, end });
  };

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleDateClick = (date) => {
    if (isDateAvailable(date)) {
      setSelectedDate(date);
      setSelectedOption(null);
      setBookedGuestsCount(0);
      setAvailableSpots(null);
      setAvailableSpotsPerOption({});
      setGuestCount(1);
    }
  };

  useEffect(() => {
    const fetchBookedGuestsForDate = async () => {
      if (!selectedDate) {
        setAvailableSpotsPerOption({});
        return;
      }
      const availableOptions = getOptionsForDate(selectedDate);
      if (availableOptions.length === 0) {
        setAvailableSpotsPerOption({});
        return;
      }
      setLoadingSpots(true);
      try {
        const dateKey = format(selectedDate, 'yyyy-MM-dd');
        const bookingsRef = collection(db, 'experienceBookings');
        const q = query(bookingsRef, where('experienceId', '==', id), where('date', '==', dateKey));
        const querySnapshot = await getDocs(q);
        const bookedPerOption = {};
        querySnapshot.forEach((docSnap) => {
          const booking = docSnap.data();
          if (booking.paymentStatus === 'completed' && booking.status !== 'cancelled') {
            const optName = booking.optionName;
            bookedPerOption[optName] = (bookedPerOption[optName] || 0) + (booking.guestCount || 0);
          }
        });
        const spotsPerOption = {};
        availableOptions.forEach((opt) => {
          const booked = bookedPerOption[opt.name] || 0;
          if (opt.type === 'Group Experience') {
            spotsPerOption[opt.name] = Math.max(0, opt.maxGuests - booked);
          } else {
            spotsPerOption[opt.name] = opt.maxGuests;
          }
        });
        setAvailableSpotsPerOption(spotsPerOption);
      } catch (error) {
        console.error('Error fetching booked guests:', error);
        setAvailableSpotsPerOption({});
      } finally {
        setLoadingSpots(false);
      }
    };
    fetchBookedGuestsForDate();
  }, [selectedDate, id]);

  useEffect(() => {
    if (selectedOption && availableSpotsPerOption[selectedOption.name] !== undefined) {
      const spots = availableSpotsPerOption[selectedOption.name];
      setAvailableSpots(spots);
      const booked = selectedOption.maxGuests - spots;
      setBookedGuestsCount(booked);
      if (spots > 0 && guestCount < selectedOption.minGuests) {
        setGuestCount(selectedOption.minGuests);
      } else if (guestCount > spots) {
        setGuestCount(Math.max(selectedOption.minGuests, spots));
      }
    }
  }, [selectedOption, availableSpotsPerOption]);

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const calendarDays = getDaysInMonth(currentMonth);
  const firstDayOfMonth = startOfMonth(currentMonth).getDay();

  if (loading) {
    return (
      <div className="experience-detail-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error || !experience) {
    return (
      <div className="experience-detail-page">
        <div className="experience-details-error">
          <h2>{error || 'Experience not found'}</h2>
          <button onClick={() => navigate('/experiences')}>Back to Experiences</button>
        </div>
      </div>
    );
  }

  const prices = getTotalPrice();

  return (
    <div className="experience-detail-page">
      <div className="experience-details-container">
        <div className="experience-gallery">
          <div className="gallery-main">
            {experience.images && experience.images.length > 0 ? (
              <>
                <img src={experience.images[currentImageIndex]} alt={experience.name} className="gallery-main-image" />
                {experience.images.length > 1 && (
                  <>
                    <button className="gallery-nav prev" onClick={prevImage}><HiOutlineChevronLeft /></button>
                    <button className="gallery-nav next" onClick={nextImage}><HiOutlineChevronRight /></button>
                    <div className="gallery-dots">
                      {experience.images.map((_, index) => (
                        <span key={index} className={`gallery-dot ${index === currentImageIndex ? 'active' : ''}`} onClick={() => setCurrentImageIndex(index)} />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="gallery-placeholder">No Image Available</div>
            )}
          </div>
          {experience.images && experience.images.length > 1 && (
            <div className="gallery-thumbnails">
              {experience.images.slice(0, 5).map((img, index) => (
                <img key={index} src={img} alt={`${experience.name} ${index + 1}`} className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`} onClick={() => setCurrentImageIndex(index)} />
              ))}
            </div>
          )}
        </div>

        <div className="experience-content">
          <div className="experience-main-info">
            <div className="experience-details-header-section">
              <div className="experience-details-category-badge">
                <span className="category-tag">{experience.category}</span>
              </div>
              <h1>{experience.name}</h1>
              {reviewStats.totalNumberOfReviews > 0 && (
                <div className="rating-display" onClick={() => document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' })}>
                  <span className="star">★</span>
                  <span className="score">{reviewStats.averageRating.toFixed(1)}</span>
                  <span className="count">({reviewStats.totalNumberOfReviews} reviews)</span>
                </div>
              )}
              <div className="experience-meta">
                <span className="meta-item"><HiOutlineMapPin />{experience.location || experience.cityRegion}</span>
                {selectedOption && (
                  <>
                    <span className="meta-item"><HiOutlineClock />{selectedOption.duration}h{selectedOption.durationMinutes ? ` ${selectedOption.durationMinutes}m` : ''}</span>
                    <span className="meta-item"><HiOutlineUsers />{selectedOption.minGuests}-{selectedOption.maxGuests} guests</span>
                  </>
                )}
              </div>
            </div>

          <div className="experience-section">
            <h3>About This Experience</h3>
            <p className="experience-description-text">{experience.description}</p>
          </div>

          {experience.highlights && (Array.isArray(experience.highlights) ? experience.highlights.length > 0 : experience.highlights) && (
            <div className="experience-section">
              <h3>Highlights</h3>
              {Array.isArray(experience.highlights) ? (
                <ul className="highlights-list">
                  {experience.highlights.map((highlight, index) => (<li key={index} className="highlight-item">{highlight}</li>))}
                </ul>
              ) : (<p className="highlights-text">{experience.highlights}</p>)}
            </div>
          )}

          <div className="experience-section host-section">
            <h3>Your Host</h3>
            <div className="host-info">
              <div className="host-avatar"><HiOutlineUser /></div>
              <div className="host-details">
                <h4>{experience.hostName}</h4>
                {experience.hostBio && <p>{experience.hostBio}</p>}
              </div>
            </div>
          </div>

          <div className="experience-section">
            <h3>Meeting Point</h3>
            <div className="meeting-point-info">
              <HiOutlineMapPin />
              <div><p className="meeting-address">Exact location will be provided after booking</p></div>
            </div>
          </div>

          {experience.itinerary && experience.itinerary.length > 0 && (
            <div className="experience-section">
              <h3>Itinerary</h3>
              <div className="itinerary-list">
                {experience.itinerary.map((item, index) => (
                  <div key={index} className="itinerary-item">
                    <div className="itinerary-step">{index + 1}</div>
                    <div className="itinerary-content">
                      <h4>{item.activity}</h4>
                      {item.description && <p>{item.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {experience.included && experience.included.length > 0 && (
            <div className="experience-section">
              <h3>What's Included</h3>
              <div className="included-grid">
                {experience.included.map((item, index) => (
                  <div key={index} className="included-item"><HiOutlineCheckCircle /><span>{item}</span></div>
                ))}
              </div>
            </div>
          )}

          {experience.notIncluded && (
            <div className="experience-section">
              <h3>Not Included</h3>
              <div className="not-included-list">
                {experience.notIncluded.split(',').map((item, index) => (
                  <div key={index} className="not-included-item"><HiOutlineXCircle /><span>{item.trim()}</span></div>
                ))}
              </div>
            </div>
          )}

          {experience.languages && experience.languages.length > 0 && (
            <div className="experience-section">
              <h3>Languages</h3>
              <div className="languages-list"><HiOutlineGlobeAlt />{experience.languages.join(', ')}</div>
            </div>
          )}

          {experience.vehicleType && experience.vehicleType !== 'No Vehicle' && (
            <div className="experience-section">
              <h3>Transportation</h3>
              <div className="vehicle-info"><HiOutlineTruck /><span>{experience.vehicleType}</span></div>
            </div>
          )}

          <div className="experience-section">
            <h3>Cancellation Policy</h3>
            <div className="policy-info"><HiOutlineInformationCircle /><span>Free cancellation up to 72 hours before</span></div>
          </div>

          {reviewStats.totalNumberOfReviews > 0 && (
            <div id="reviews-section" className="reviews-section">
              <h3>Reviews</h3>
              <div className="reviews-summary">
                <span className="star">★</span>
                <span className="score">{reviewStats.averageRating.toFixed(1)}</span>
                <span className="count">({reviewStats.totalNumberOfReviews})</span>
              </div>
              <div className="rating-bars">
                <div className="rating-bar-item">
                  <div className="rating-bar-label"><span>Cleanliness</span><span>{formatRating(reviewStats.totalCleanlinessPoints, reviewStats.totalNumberOfReviews)}</span></div>
                  <div className="rating-bar-track"><div className="rating-bar-fill" style={{ width: `${calculatePercentage(reviewStats.totalCleanlinessPoints, reviewStats.totalNumberOfReviews)}%` }} /></div>
                </div>
                <div className="rating-bar-item">
                  <div className="rating-bar-label"><span>Communication</span><span>{formatRating(reviewStats.totalCommunicationPoints, reviewStats.totalNumberOfReviews)}</span></div>
                  <div className="rating-bar-track"><div className="rating-bar-fill" style={{ width: `${calculatePercentage(reviewStats.totalCommunicationPoints, reviewStats.totalNumberOfReviews)}%` }} /></div>
                </div>
                <div className="rating-bar-item">
                  <div className="rating-bar-label"><span>Service</span><span>{formatRating(reviewStats.totalServicePoints, reviewStats.totalNumberOfReviews)}</span></div>
                  <div className="rating-bar-track"><div className="rating-bar-fill" style={{ width: `${calculatePercentage(reviewStats.totalServicePoints, reviewStats.totalNumberOfReviews)}%` }} /></div>
                </div>
              </div>
              <div className="reviews-list">
                {reviews.slice(0, visibleReviews).map(review => (
                  <div key={review.id} className="review-item">
                    <div className="review-header">
                      <span className="review-author">{review.reviewerName}</span>
                      <div className="review-rating"><span className="star">★</span><span>{review.ratings?.general || 0} / 5</span></div>
                    </div>
                    <div className="review-date">{review.createdAt?.toLocaleDateString()}</div>
                    <p className="review-message">{review.message}</p>
                  </div>
                ))}
                {reviews.length > visibleReviews && (
                  <button className="see-more-btn" onClick={() => setVisibleReviews(prev => prev + 3)}>See More Reviews</button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="booking-sidebar">
          <div className="booking-card">
            <div className="booking-price">
              <span className="price-label">From</span>
              <span className="price-amount">€{getDisplayPrice().price}</span>
              <span className="price-unit">{getDisplayPrice().isPrivate ? '/group' : '/person'}</span>
              <span className="price-post-tag">+post</span>
            </div>

            {!isAuthenticated && (<div className="booking-alert info"><HiOutlineInformationCircle />Sign in to see affiliate pricing</div>)}
            {isAuthenticated && !isApproved && (<div className="booking-alert warning"><HiOutlineInformationCircle />Get approved to book at affiliate prices</div>)}
            {isApproved && (<div className="tier-badge"><HiOutlineCheckBadge />Tier {affiliateTier} Affiliate</div>)}

            {options.length > 0 && (
              <div className="booking-field">
                <label>Select Date</label>
                <div className="booking-calendar">
                  <div className="calendar-header">
                    <button type="button" onClick={handlePrevMonth}><HiOutlineChevronLeft /></button>
                    <span>{format(currentMonth, 'MMMM yyyy')}</span>
                    <button type="button" onClick={handleNextMonth}><HiOutlineChevronRight /></button>
                  </div>
                  <div className="calendar-grid">
                    {daysOfWeek.map((day) => (<div key={day} className="calendar-day-header">{day}</div>))}
                    {Array.from({ length: firstDayOfMonth }).map((_, index) => (<div key={`empty-${index}`} className="calendar-day empty" />))}
                    {calendarDays.map((day, index) => {
                      const isAvailable = isDateAvailable(day);
                      const isSelected = selectedDate && isSameDay(day, selectedDate);
                      return (
                        <div key={index} className={`calendar-day ${!isSameMonth(day, currentMonth) ? 'different-month' : ''} ${isSelected ? 'selected' : ''} ${isAvailable ? 'available' : 'unavailable'}`} onClick={() => handleDateClick(day)}>
                          {format(day, 'd')}
                        </div>
                      );
                    })}
                  </div>
                  {selectedDate && (<div className="selected-date-info"><span>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span></div>)}
                </div>
              </div>
            )}

            {selectedDate && availableOptionsForSelectedDate.length > 0 && (
              <div className="booking-field">
                <label>Select Option for {format(selectedDate, 'MMM d')}</label>
                <div className="option-radio-list">
                  {availableOptionsForSelectedDate.map((opt, index) => {
                    const isSoldOut = opt.type === 'Group Experience' && availableSpotsPerOption[opt.name] !== undefined && availableSpotsPerOption[opt.name] < opt.minGuests;
                    const affiliatePrice = getAffiliatePriceForOption(opt);
                    const affiliateMinPrice = getAffiliateMinPriceForOption(opt);
                    const regularPrice = getRegularPriceForOption(opt);
                    // For Private Experience, show min price; for Group, show per-person price
                    const displayAffPrice = opt.type === 'Private Experience' && affiliateMinPrice !== null ? affiliateMinPrice : affiliatePrice;
                    return (
                      <div key={index} className={`option-radio-item ${selectedOption === opt ? 'selected' : ''} ${isSoldOut ? 'disabled' : ''}`} onClick={() => !isSoldOut && setSelectedOption(opt)}>
                        <div className="option-radio-circle">{selectedOption === opt && <div className="option-radio-dot" />}</div>
                        <div className="option-radio-content">
                          <span className="option-name">{opt.name || opt.type}</span>
                          <span className="option-details">Start: {opt.startTime} • {opt.duration}h{opt.durationMinutes ? ` ${opt.durationMinutes}m` : ''}</span>
                          <span className="option-details">{opt.minGuests}-{opt.maxGuests} guests</span>
                          {opt.type === 'Group Experience' && !loadingSpots && availableSpotsPerOption[opt.name] !== undefined && (
                            <span className={`option-availability ${availableSpotsPerOption[opt.name] < opt.minGuests ? 'sold-out' : availableSpotsPerOption[opt.name] <= 5 ? 'limited' : ''}`}>
                              {availableSpotsPerOption[opt.name] < opt.minGuests ? 'Sold Out' : `${availableSpotsPerOption[opt.name]} spots left`}
                            </span>
                          )}
                          <span className="option-price">
                            {displayAffPrice !== null && isApproved ? (
                              <><span className="original-price">€{regularPrice}</span> <span className="affiliate-price">{displayAffPrice === 0 ? 'FREE' : `€${displayAffPrice}`}</span></>
                            ) : (
                              <>€{regularPrice}{opt.type === 'Group Experience' ? '/person' : ' total'}</>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedOption && (
              <div className="booking-field">
                <label>Number of Guests</label>
                {loadingSpots ? (<div className="spots-loading">Checking availability...</div>) : availableSpots !== null && availableSpots < selectedOption.minGuests ? (
                  <div className="no-spots-available"><HiOutlineXCircle /><span>Not enough spots available</span></div>
                ) : (
                  <>
                    <div className="guest-counter">
                      <button onClick={() => setGuestCount(Math.max(selectedOption.minGuests, guestCount - 1))} disabled={guestCount <= selectedOption.minGuests}>-</button>
                      <span>{guestCount}</span>
                      <button onClick={() => setGuestCount(Math.min(availableSpots || selectedOption.maxGuests, guestCount + 1))} disabled={guestCount >= (availableSpots || selectedOption.maxGuests)}>+</button>
                    </div>
                    <span className="guest-range">Min: {selectedOption.minGuests} / Max: {availableSpots !== null ? availableSpots : selectedOption.maxGuests}</span>
                  </>
                )}
              </div>
            )}

            {selectedOption && (
              <div className="booking-total">
                <span>Total</span>
                <div className="total-prices">
                  {prices.affiliate !== null && isApproved ? (
                    <>
                      <span className="total-original">€{prices.regular}</span>
                      <span className="total-amount">{prices.affiliate === 0 ? 'FREE' : `€${prices.affiliate}`}</span>
                    </>
                  ) : (
                    <span className="total-amount">€{prices.regular}</span>
                  )}
                </div>
              </div>
            )}

            <button className="book-now-btn" onClick={handleBookNow} disabled={!selectedOption || options.length === 0 || loadingSpots || (availableSpots !== null && availableSpots < (selectedOption?.minGuests || 1)) || !isApproved}>
              {!isAuthenticated ? 'Sign In to Book' : !isApproved ? 'Get Approved to Book' : options.length === 0 ? 'No Options Available' : (availableSpots !== null && availableSpots < (selectedOption?.minGuests || 1)) ? 'Not Enough Spots' : 'Book Now'}
            </button>
            <p className="booking-note"><HiOutlineInformationCircle />You won't be charged yet</p>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default ExperienceDetailPage;
